import { COUNTED, CORPUS } from './chorales/corpusIndex';
import { CADENCE_NAMES, CadenceKind } from './chorales/derive';
import { chordFamily } from './observatory';
import { Instance, instanceOf, instancesOf } from './corpus';
import { pathById } from './index';
import { tuneRoom } from './tunes';
import { keyName, parseKey, sameKey } from '../music/key';
import { mod, pcNumber } from '../music/pitch';

/**
 * Ear-training questions, built from the corpus by rule: every excerpt is real music from the
 * atlas and every right answer is what the analysis and the site's rules say. Nothing is
 * generated; the only chance involved is which excerpt comes next.
 */

export type ExerciseId = 'endings' | 'mode' | 'same' | 'pattern';

export interface Choice {
  id: string;
  label: string;
  /** Shown under the label at the Names level and above. */
  name?: string;
}

export interface Question {
  exercise: ExerciseId;
  prompt: string;
  choices: Choice[];
  answer: string;
  /** Excerpts to play one after another. */
  clips: Instance[];
  /** Move every clip to C major / A minor (so the key gives nothing away). */
  normalize: boolean;
  /** What the right answer was and why, in plain words; `named` adds the theory name. */
  explain: string;
  named?: string;
  /** Where to hear it in context. */
  link: { href: string; label: string };
}

export const EXERCISES: Array<{ id: ExerciseId; title: string; blurb: string; minDepth?: number }> = [
  { id: 'endings', title: 'How does it end?', blurb: 'Hear a phrase of a Bach chorale and say how it ends: at home, on a question, or with the ground giving way.' },
  { id: 'mode', title: 'Bright or dark?', blurb: 'Hear the opening of a chorale and say whether its home is major or minor.' },
  { id: 'same', title: 'Same harmony or not?', blurb: 'Two settings of one melody note, one after the other. Did Bach put the same chord under it?' },
  { id: 'pattern', title: 'Which pattern?', blurb: 'Hear a stock progression in real music and pick which of three it is.' },
];

type Rand = () => number;
const pick = <T,>(list: T[], r: Rand): T => list[Math.floor(r() * list.length)];
/** Fisher–Yates: every order equally likely. */
function shuffle<T>(list: T[], r: Rand): T[] {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(r() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/* ---------- How does it end? ---------- */

const ENDING_CHOICES: Record<string, Choice> = {
  pac: { id: 'pac', label: 'Arrives home: a full stop', name: CADENCE_NAMES.pac },
  iac: { id: 'iac', label: 'Arrives, but softly', name: CADENCE_NAMES.iac },
  half: { id: 'half', label: 'Stops on a question', name: CADENCE_NAMES.half },
  deceptive: { id: 'deceptive', label: 'The ground gives way', name: CADENCE_NAMES.deceptive },
};

const EXPLAIN_ENDING: Record<string, string> = {
  pac: 'The pull resolves completely: the bass falls from the fifth note to home and the melody lands on the keynote.',
  iac: 'It arrives home, but the melody lands on another note of the home chord, so the full stop is softer.',
  half: 'The phrase stops on the chord that pulls hardest toward home, and waits there: a question left open.',
  deceptive: 'Everything prepares the arrival home, then a different chord takes its place: the floor gives way.',
};

let endingPool: Map<string, Array<{ id: string; i: number }>> | null = null;
function endings(): Map<string, Array<{ id: string; i: number }>> {
  if (endingPool) return endingPool;
  endingPool = new Map();
  for (const e of COUNTED) {
    e.cadences.forEach((c, i) => {
      const kind = c[2] as CadenceKind;
      if (!ENDING_CHOICES[kind]) return;
      const [start] = e.phrases[i] ?? [0, 0];
      if (c[0] - start < 3) return; // too short to hear the approach
      const list = endingPool!.get(kind) ?? [];
      list.push({ id: e.id, i });
      endingPool!.set(kind, list);
    });
  }
  return endingPool;
}

function endingQuestion(r: Rand, harder: boolean): Question {
  const kinds = harder ? ['pac', 'iac', 'half', 'deceptive'] : ['pac', 'half', 'deceptive'];
  const kind = pick(kinds, r);
  const { id, i } = pick(endings().get(kind)!, r);
  const e = COUNTED.find((x) => x.id === id)!;
  const [at, dur, , key] = e.cadences[i];
  const [phraseStart] = e.phrases[i];
  const start = Math.max(phraseStart, at - 10);
  // Stop after the arrival chord: any longer and the next phrase's first chord gives the answer away.
  const clip = { ...instanceOf([id, start, at + Math.min(dur, 2), key]), momentId: `phrase-${i + 1}` };
  return {
    exercise: 'endings',
    prompt: 'How does this phrase end?',
    choices: kinds.map((k) => ENDING_CHOICES[k]),
    answer: kind,
    clips: [clip],
    normalize: true,
    explain: EXPLAIN_ENDING[kind],
    named: `${CADENCE_NAMES[kind as CadenceKind]} in ${keyName(parseKey(key))}.`,
    link: { href: `#${id}.phrase-${i + 1}`, label: `${e.title} (${e.catalogue}), phrase ${i + 1}` },
  };
}

/* ---------- Bright or dark? ---------- */

let modePool: Map<string, typeof COUNTED> | null = null;
/** Chorales whose first phrase stays at home and ends there: what the ear hears is the home mode. */
function modeChorales(mode: string): typeof COUNTED {
  if (!modePool) {
    modePool = new Map([
      ['major', []],
      ['minor', []],
    ]);
    for (const e of COUNTED) {
      const home = parseKey(e.homeKey);
      const first = e.cadences[0];
      if (!first || !e.phrases.length || !['pac', 'iac', 'half'].includes(first[2])) continue;
      if (!sameKey(parseKey(first[3]), home)) continue;
      modePool.get(home.mode)?.push(e);
    }
  }
  return modePool.get(mode) ?? [];
}

function modeQuestion(r: Rand): Question {
  const mode = r() < 0.5 ? 'major' : 'minor';
  const pool = modeChorales(mode);
  const e = pick(pool, r);
  const [start, end] = e.phrases[0];
  const clip = { ...instanceOf([e.id, start, Math.min(end, start + 18), e.homeKey]), momentId: 'phrase-1' };
  return {
    exercise: 'mode',
    prompt: 'Is home bright (major) or dark (minor)?',
    choices: [
      { id: 'major', label: 'Bright', name: 'Major' },
      { id: 'minor', label: 'Dark', name: 'Minor' },
    ],
    answer: mode,
    clips: [clip],
    normalize: false,
    explain: mode === 'major' ? 'Home is a major chord: its third is four semitones above the keynote, and it sounds open and bright.' : 'Home is a minor chord: its third is only three semitones above the keynote, and it sounds darker.',
    named: `${keyName(parseKey(e.homeKey))}.`,
    link: { href: `#${e.id}.phrase-1`, label: `${e.title} (${e.catalogue}), first phrase` },
  };
}

/* ---------- Same harmony or not? ---------- */

/** The chords under one melody note, start to finish (a note can carry a change of chord). */
function chordsUnder(s: NonNullable<ReturnType<typeof tuneRoom>>['settings'][number], c: number): Array<{ id: string; rn: string; key: ReturnType<typeof parseKey> }> {
  const cell = s.cells[c];
  const out: Array<{ id: string; rn: string; key: ReturnType<typeof parseKey> }> = [];
  for (const ev of s.view.events) {
    if (ev.end <= cell.onset + 1e-6 || ev.start >= cell.onset + cell.dur - 1e-6) continue;
    const id = `${mod(pcNumber(ev.chord.root) + s.shift, 12)}:${ev.chord.triad}`;
    if (out.length && out[out.length - 1].id === id) continue;
    out.push({ id, rn: ev.spec.rn, key: ev.key });
  }
  return out;
}

function sameQuestion(r: Rand, words: (rn: string) => string): Question | null {
  const want = r() < 0.5 ? 'same' : 'different';
  const tunes = CORPUS.tunes.filter((t) => t.ids.length >= 2);
  for (let tries = 0; tries < 30; tries++) {
    const t = pick(tunes, r);
    const room = tuneRoom(t.slug);
    if (!room || room.settings.length < 2) continue;
    const pairs: Array<[number, number, number]> = [];
    for (let c = 0; c < room.columns; c++) {
      for (let a = 0; a < room.settings.length; a++)
        for (let b = a + 1; b < room.settings.length; b++) {
          const x = room.settings[a].cells[c];
          const y = room.settings[b].cells[c];
          // Both settings sing the reference's note here (not a variant), long enough to hear the chord.
          if (x.note === null || y.note === null || !x.same || !y.same || x.dur < 0.9 || y.dur < 0.9) continue;
          const cx = chordsUnder(room.settings[a], c).map((h) => h.id).join(' ');
          const cy = chordsUnder(room.settings[b], c).map((h) => h.id).join(' ');
          if (!cx || !cy) continue;
          if ((cx === cy) === (want === 'same')) pairs.push([c, a, b]);
        }
    }
    if (!pairs.length) continue;
    const [c, ai, bi] = pick(pairs, r);
    const [sa, sb] = r() < 0.5 ? [room.settings[ai], room.settings[bi]] : [room.settings[bi], room.settings[ai]];
    // Just the note: a lead-in would bring chords of its own, and the question is about this one.
    const clip = (s: typeof sa) => {
      const cell = s.cells[c];
      const base = instanceOf([s.id, cell.onset, cell.onset + cell.dur, s.piece.homeKey]);
      return { ...base, shift: s.shift, label: `${s.piece.catalogue}` };
    };
    const ha = chordsUnder(sa, c);
    const hb = chordsUnder(sb, c);
    const said = (h: typeof ha) => h.map((x) => words(x.rn)).join(', then ');
    const named = (h: typeof ha) => h.map((x) => `${x.rn} in ${keyName(x.key)}`).join(', then ');
    const same = want === 'same';
    return {
      exercise: 'same',
      prompt: 'The same melody note, first in one setting, then in another. Is the harmony under it the same?',
      choices: [
        { id: 'same', label: 'The same harmony' },
        { id: 'different', label: 'A different harmony' },
      ],
      answer: want,
      clips: [clip(sa), clip(sb)],
      normalize: false,
      explain: same ? `Both settings put ${said(ha)} under this note.` : `The first puts ${said(ha)} under it; the second, ${said(hb)}.`,
      named: same
        ? named(ha) === named(hb)
          ? `Both: ${named(ha)}.`
          : `The same chord, read in different keys: ${named(ha)}, and ${named(hb)}.`
        : `${named(ha)}, against ${named(hb)}.`,
      link: { href: `#tune.${room.slug}.${c + 1}`, label: `Every setting of this note, in the tune room` },
    };
  }
  return null;
}

/* ---------- Which pattern? ---------- */

const PATTERN_POOL = ['authentic-cadence', 'half-cadence', 'deceptive-cadence', 'prinner', 'romanesca', 'fonte', 'monte', 'quiescenza', 'fifths-sequence', 'ponte'];

const clipCache = new Map<string, Instance[]>();
/** Excerpts of a pattern to quiz on: a beat or two of lead-in, and long enough to hear (two beats or more). */
function patternClips(id: string): Instance[] {
  let list = clipCache.get(id);
  if (!list) {
    list = instancesOf(id)
      .filter((x) => (x.kind === 'chorale' || x.kind === 'sonata' || x.kind === 'variations' || x.kind === 'prelude') && x.end - x.start >= 2 - 1e-6)
      .map((x) => ({ ...x, start: Math.max(0, x.start - 2) }));
    clipCache.set(id, list);
  }
  return list;
}

function patternQuestion(r: Rand, named: boolean): Question | null {
  const usable = PATTERN_POOL.filter((id) => pathById(id) && patternClips(id).length >= 3);
  if (usable.length < 3) return null;
  const target = pick(usable, r);
  const others = usable.filter((x) => x !== target);
  const d1 = pick(others, r);
  const d2 = pick(others.filter((x) => x !== d1), r);
  const ids = shuffle([target, d1, d2], r);
  const inst = pick(patternClips(target), r);
  const path = pathById(target)!;
  return {
    exercise: 'pattern',
    prompt: 'Which pattern is this?',
    choices: ids.map((id) => {
      const p = pathById(id)!;
      return { id, label: named ? p.name : p.feeling, name: named ? p.feeling : p.name };
    }),
    answer: target,
    clips: [inst],
    normalize: true,
    // The verdict already names the answer's label (the feeling, below the Names level): don't say it twice.
    explain: named ? `${path.feeling}. ${path.plain ?? path.listenFor}` : (path.plain ?? path.listenFor),
    named: path.name,
    link: { href: `#path.${target}`, label: `Hear the ${named ? path.name.toLowerCase() : 'pattern'} on its own` },
  };
}

export function makeQuestion(exercise: ExerciseId, r: Rand, opts: { harder: boolean; depth: number; words: (rn: string) => string }): Question {
  const q =
    exercise === 'endings'
      ? endingQuestion(r, opts.harder)
      : exercise === 'mode'
        ? modeQuestion(r)
        : exercise === 'same'
          ? sameQuestion(r, opts.words)
          : patternQuestion(r, opts.depth >= 2);
  return q ?? endingQuestion(r, false);
}

/** Plain words for a chord (by family), for explanations. */
export function chordWords(rn: string, table: Record<string, string>): string {
  return table[chordFamily(rn)] ?? 'another chord';
}
